import { Economy, LevelCatalog } from "@highmarch/app";
import { BalanceData, RARITY_IDS, primaryValue } from "@highmarch/domain";
import { btnHtml, fmtCompact } from "../kit";
import { uiUrl } from "../portraits";
import { RARITY_LABEL, fmtStat, pct } from "../gearUi";
import { toast } from "../toast";
import type { HubApi } from "../../features/registry";
import { priceHtml } from "./econArt";
import { currencyPillsHtml, emptyHtml, flagHtml, heroHtml, lockChipHtml, priceButtonHtml, rowHtml, secHtml, sheetHeadHtml } from "../components";
import { MarketModel, OfferCard, SellRow, marketModel } from "./marketModel";
import { confirmPurchase, econCue, flyCurrency, purchasePop, tickPill } from "./purchaseFx";
import { refusalText } from "./shopActions";
import { mountBlackMarketSkin } from "./skinShelves";
import { openShops } from "../shops/shopsScreen";
import { HERO_FOCUS, MERCHANT_PORTRAIT, MERCHANT_STALL } from "../shops/shopsArt";
import { gearWellHtml, itemBlockHtml } from "./itemBlock";
import { SHOP_SKIN } from "../shopSkin";

/**
 * More → Market (FINISH_PLAN WP5; owner decision D7; M13, M14): the Mystery Merchant's door (shop pass 2, docs/SHOP.md
 * §9.2: he sells the Trading Post's gear and more, on the Shops screen), selling spare Rare+ gear for Marks (always
 * for more than salvage) and the Black Market (one featured Legendary or Mythic a day, for gems; no countdown, no
 * push). Every trade confirms and gives a receipt. Hub only.
 */
export type MarketTab = "buy" | "sell" | "black";

export interface MarketDeps { readonly hub: HubApi; readonly catalog: LevelCatalog; now(): number }

/** How long a bought card's stamp plays before the page redraws. */
const REVEAL_MS = 700;

export function openMarket(d: MarketDeps, tab: MarketTab = "buy"): HTMLElement {
  const sheet = document.createElement("div");
  sheet.className = `sheet panel market more ${SHOP_SKIN}`;
  const B = d.catalog.balance, eco = () => new Economy(d.catalog), parent = d.hub.parent;
  const render = (): void => {
    const m = marketModel(d.catalog, d.now());
    sheet.dataset.tab = tab;
    sheet.innerHTML = `
      ${sheetHeadHtml({ title: "Market", sub: "Trade spare gear for Marks", portrait: uiUrl(MERCHANT_PORTRAIT), tint: "wood",
        extra: currencyPillsHtml([{ currency: "marks", amount: fmtCompact(m.marks) }, { currency: "gems", amount: fmtCompact(d.catalog.economy.wallet.gems) }]) })}
      <div class="seg2 mk-tabs" role="tablist">${tabBtn("buy", "Merchant", tab)}${tabBtn("sell", `Sell <small>${m.sellable.length}</small>`, tab)}${tabBtn("black", "Black Market", tab)}</div>
      <div class="mk-body">${tab === "buy" ? merchantDoorHtml(d.catalog) : tab === "sell" ? sellHtml(B, m) : blackHtml(B, m)}</div>`;
    if (tab === "black") mountBlackMarketSkin(sheet.querySelector<HTMLElement>(".mk-body")!, parent, render);   // WP8: an exclusive skin, some days
    sheet.querySelector<HTMLButtonElement>(".close")!.onclick = () => sheet.remove();
    for (const b of sheet.querySelectorAll<HTMLButtonElement>(".mk-tabs .sg")) b.onclick = () => { if (b.dataset.t !== tab) { tab = b.dataset.t as MarketTab; render(); } };
    for (const b of sheet.querySelectorAll<HTMLButtonElement>(".mbuy")) b.onclick = () => void buy(m, b);
    sheet.querySelector<HTMLButtonElement>(".mkdoor .go")?.addEventListener("click", () => { openShops("merchant"); });
    for (const b of sheet.querySelectorAll<HTMLButtonElement>(".msell")) b.onclick = () => void sell(m, Number(b.dataset.id), b);
  };

  const buy = async (m: MarketModel, b: HTMLButtonElement): Promise<void> => {
    const c = b.dataset.bm !== undefined ? m.blackMarket : null;       // the Trading Post's stock moved to the Merchant
    if (!c || c.bought) return;
    const cur = c.offer.currency;
    if (!c.affordable) { toast(parent, `You need ${fmtCompact(c.short)} more ${cur === "gems" ? "gems" : "Marks"}${cur === "marks" ? ": sell spare gear for more" : ""}`); return; }
    const ok = await confirmPurchase(parent, { shop: true, title: cur === "gems" ? "BLACK MARKET" : "TRADING POST", currency: cur, price: c.offer.price,
      body: `${itemBlockHtml(B, c.offer.item)}<p>${cur === "gems" ? "It goes to your Gear stash, bound to you." : "It goes to your Gear stash."}</p>` });
    if (!ok) return;
    const before = cur === "gems" ? eco().wallet.gems : eco().wallet.marks;
    const r = eco().buyBlackMarket(c.offer, d.now(), "hub");
    if (typeof r === "string") { toast(parent, refusalText(r)); render(); return; }
    econCue(cur === "gems" ? "buy" : "trade");      // the Black Market is a gem spend; the Trading Post a trade
    purchasePop(b.closest(".bmcard"));
    d.hub.changed();
    tickPill(cur, before, cur === "gems" ? eco().wallet.gems : eco().wallet.marks);
    toast(parent, `${RARITY_LABEL[r.rarity]} ${B.gear.slots[r.slot].name} is in your Gear stash`);
    window.setTimeout(render, REVEAL_MS);
  };

  const sell = async (m: MarketModel, id: number, b: HTMLButtonElement): Promise<void> => {
    const row = m.sellable.find((s) => s.item.id === id);
    if (!row) return;
    const ok = await confirmSale(parent, B, row, true);
    if (!ok) return;
    const before = eco().wallet.marks;
    const r = eco().sellToTradingPost(id, d.now(), row.mythic);
    if (typeof r === "string") { toast(parent, refusalText(r)); render(); return; }
    econCue("sell");
    d.hub.changed();
    flyCurrency(b, "marks", before, eco().wallet.marks);
    toast(parent, `Sold for ${r.marks} Marks`);
    render();
  };

  render();
  parent.append(sheet);
  return sheet;
}

/** The sale confirm (also the Armoury item card's Sell): what goes, what it pays, and that a Mythic's effect goes with it.
 * `shop`: opened from the Market, so it wears the shop's skin (the Armoury's keeps the game's look). */
export function confirmSale(parent: HTMLElement, B: BalanceData, row: SellRow, shop = false): Promise<boolean> {
  const mythic = row.mythic && row.item.mythicEffect >= 0 ? `<p class="warn">${B.gear.mythicEffects[row.item.mythicEffect].name} goes with it, for good.</p>` : "";
  return confirmPurchase(parent, { shop, title: "SELL", currency: "marks", price: row.marks, confirm: "SELL",
    body: `${itemBlockHtml(B, row.item)}${mythic}<p>Salvage would give ${fmtCompact(row.salvageTotal)} gold. The Trading Post pays more.</p>` });
}

const tabBtn = (t: MarketTab, label: string, on: MarketTab) =>
  `<button type="button" role="tab" class="sg${t === on ? " on" : ""}" data-t="${t}" aria-selected="${t === on}">${label}</button>`;

function offerBtn(c: OfferCard, data: string): string {
  if (c.bought) return `<span class="k-stamp">Sold · a new piece tomorrow</span>`;
  return priceButtonHtml({ currency: c.offer.currency, amount: fmtCompact(c.offer.price), short: !c.affordable, className: "mbuy", data });
}

/** Market → Merchant: the Mystery Merchant's hero (the same art as the Shops sheet) and VISIT, or when he comes. */
function merchantDoorHtml(c: LevelCatalog): string {
  const open = c.shops.unlocked("merchant"), s = c.balance.shops.merchant;
  const side = open ? btnHtml("VISIT", "gold", { className: "go" }) : lockChipHtml(`Clear level ${s.unlockAfterLevel}`);
  return `${heroHtml({ art: uiUrl(MERCHANT_STALL), kicker: "Mystery Merchant", title: `${s.slots} goods a day`, blurb: "The Trading Post's gear, some at -20% or -50%", side, focus: HERO_FOCUS, className: `shero mkdoor${open ? "" : " closed"}` })}
    <p class="foot">Marks come from your spare gear: sell it on the Sell tab.</p>`;
}

function sellHtml(B: BalanceData, m: MarketModel): string {
  const rows = m.sellable.map((s) => rowHtml({
    lead: gearWellHtml(B, s.item, "sm"), className: "srow",
    title: `<span class="ri-${RARITY_IDS[s.item.rarity]}">${RARITY_LABEL[s.item.rarity]} ${B.gear.slots[s.item.slot].name} Lv ${s.item.level}</span>`,
    sub: `${pct(primaryValue(B, s.item))} ${fmtStat(s.item.primaryStat)} · salvage ${fmtCompact(s.salvageTotal)}`,
    end: `<button class="btn blue msell" data-id="${s.item.id}"><span class="disp">SELL</span>${priceHtml("marks", fmtCompact(s.marks), 15)}</button>`,
  })).join("");
  const held = m.heldBack > 0 ? `<p class="foot">${m.heldBack} more ${m.heldBack === 1 ? "is" : "are"} worn, locked or bound: take ${m.heldBack === 1 ? "it" : "them"} off or unlock to sell.</p>` : "";
  const minLabel = RARITY_LABEL[B.commerce.market.minSellRarity];
  return `${secHtml("Sell spare gear", `${minLabel} and better · pays more than salvage`)}
    ${rows ? `<div class="slist">${rows}</div>` : emptyHtml(uiUrl("empty/empty_gear.png"), `No spare ${minLabel} gear right now. Win chests, then come back.`)}${held}`;
}

function blackHtml(B: BalanceData, m: MarketModel): string {
  const c = m.blackMarket;
  if (!c) return `<p class="none">The Black Market is closed today.</p>`;
  const it = c.offer.item;
  return `<div class="k-card plate bmcard${c.bought ? " sold" : ""}${m.nudges && !c.bought ? " featured" : ""}">${flagHtml("Today's piece")}
      <div class="bmstage">${gearWellHtml(B, it, "lg")}</div>
      ${itemBlockHtml(B, it, false)}
      <div class="bmbuy">${offerBtn(c, "data-bm")}</div></div>
    <p class="foot">One Legendary or Mythic piece a day, for gems: the only gear gems can buy. A new piece every day.</p>`;
}
